import { FC } from 'react'
import Input from '@/components/Input'
import { apiClient } from 'src/apiMiddleware'
import { zodResolver } from '@hookform/resolvers/zod';
import schema from '@/validations/simInquirySchema'
import { useForm, useController, UseControllerProps } from 'react-hook-form';
import { classNames } from '@/utils'
import { PrivacyPolicy } from '@/route';

type Props = {
  toThanks: () => void
}

type FormValues = {
  company: string
  email: string
  department: string
  firstName: string
  lastName: string
  phone: string
  expectStartDate: string
  expectNumberOfBands: string
  inquiryDetails: string
  termCheck: boolean
}

type UseFormType = UseControllerProps<FormValues>

const Component: FC<Props> = ({ toThanks }) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "all",
  });

  const { field: { value, ...input }, fieldState: { invalid } } = useController({ name: 'termCheck', control })

  const onSubmit = async (data: FormValues) => {
    try {
      await apiClient.api.sim_inquiry.$post({ body: data })
      reset()
      toThanks()
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col items-center gap-10" >
      <div className="w-full flex flex-col gap-5">
        <div className="w-full flex gap-8 items-center py-2 border-b border-divided">
          <p className="text-xl font-semibold">会社情報</p>
          <p className="text-secondary-700">【必須】</p>
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="会社名" placeholder="DXHUB株式会社" name="company" control={control} />
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="部署名" placeholder="IoT事業部" name="department" control={control} />
        </div>
      </div>

      <div className="w-full flex flex-col gap-5"> <div className="w-full flex gap-8 items-center py-2 border-b border-divided"> <p className="text-xl font-semibold">担当者情報</p> <p className="text-secondary-700">【必須】</p>
      </div>

        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextSeparateForm label="氏名" placeholderFirst='姓' placeholderLast="名" lastName='lastName' firstName='firstName' control={control} name="firstName" />
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="メールアドレス" placeholder="お勤め先のメールアドレス" name="email" control={control} />
        </div>
        <div className="w-full flex flex-col items-start md:items-center md:flex-row md:justify-between">
          <TextForm label="電話番号" placeholder="繋がりやすい電話番号" name="phone" control={control} />
        </div>
      </div>

      <div className="w-full flex flex-col gap-5">
        <div className="w-full flex gap-8 items-start md:items-center py-2 border-b border-divided">
          <p className="text-xl font-semibold">事前相談内容</p>
        </div>

        <div className="w-full flex flex-col items-start md:items-start md:flex-row md:justify-between">
          <RadioForm label="利用開始時期" options={[{ label: '今すぐ', value: 0 }, { label: '1ヶ月以内', value: 1 }, { label: '3ヶ月以内', value: 2 }, { label: '半年以内', value: 4 }, { label: '1年以内', value: 5 }]} name="expectStartDate" control={control} />
        </div>
        <div className="w-full flex flex-col items-start md:items-start md:flex-row md:justify-between">
          <RadioForm label="導入予定回線数" options={[{ label: '1~10枚', value: 0 }, { label: '11~100枚', value: 1 }, { label: '101~1000枚', value: 2 }, { label: '1001枚以上', value: 4 }]} name="expectNumberOfBands" control={control} />
        </div>
        <div className="w-full flex flex-col items-start md:items-start md:flex-row md:justify-between">
          <TextAreaForm label="問い合わせ内容" placeholder="相談したい内容を記述ください" name="inquiryDetails" control={control} />
        </div>

        <div className="w-full flex flex-col items-start md:flex-row md:justify-between">
          <div />
          <div className="w-full md:w-[378px] items-center flex gap-2 md:gap-4">
            <input {...input}
              type="checkbox"
              className={classNames(
                "accent-primary-700",
                invalid
                  ? "ring-red-500 ring-4"
                  : ''
              )
              }
            />
            <div>
              <a className='text-blue-500' href={PrivacyPolicy} rel="noreferrer" target='_blank'>個人情報の取り扱いについて</a>
              <span>に同意する</span>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col items-center md:items-start md:flex-row md:justify-between">
          <div />
          <div className="w-full flex justify-center md:justify-start md:w-[378px]">
            <button disabled={!isValid || isSubmitting} type="submit" className="w-1/2 bg-accent-500 text-xl leading-none py-5 px-6 rounded-full shadow hover:shadow-md disabled:bg-gray-500/50">
              {isSubmitting
                ? <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24" >
                  <circle className="opacity-5" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                : <span>送信する</span>
              }
            </button>
          </div>
        </div>
      </div>
    </form >
  )
}

const TextForm: FC<{ label: string, placeholder: string } & UseFormType> = ({ label, placeholder, ...props }) => {
  const { field: input, fieldState: { invalid, error } } = useController(props)
  const message = error?.message === 'Required' ? '入力しましょう' : error?.message
  return (
    <>
      <label className="text-xl mb-2 md:mb-0">{label}</label>
      {/*@ts-ignore*/}
      <Input {...input} placeholder={placeholder} containerClass='bg-gray-100 w-full md:w-[378px] h-10' inputClass='bg-gray-100 px-4 text-lg' invalid={invalid} message={message} />
    </>
  )
}

const TextSeparateForm: FC<{ label: string, placeholderFirst: string, placeholderLast: string, lastName: 'lastName', firstName: 'firstName' } & UseFormType> = ({ label, placeholderFirst, placeholderLast, firstName, lastName, control }) => {
  const { field: firstField, fieldState: { invalid: ffInvalid } } = useController({ name: firstName, control })
  const { field: lastField, fieldState: { invalid: lfInvalid } } = useController({ name: lastName, control })
  return (
    <>
      <label className="text-xl">{label}</label>
      <div className="flex gap-4 w-full mt-2 md:mt-0 md:w-[378px]">
        <Input placeholder={placeholderFirst} containerClass='bg-gray-100 h-10' inputClass='bg-gray-100 px-4 text-lg' {...lastField} invalid={lfInvalid} />
        <Input placeholder={placeholderLast} containerClass='bg-gray-100 h-10' inputClass='bg-gray-100 px-4 text-lg' {...firstField} invalid={ffInvalid} />
      </div>
    </>
  )
}

const RadioForm: FC<{ label: string, options: { label: string, value: number }[] } & UseFormType> = ({ label, options, ...props }) => {
  const { field: input } = useController(props)
  return (<>
    <label className="text-xl">{label}</label>
    <div className="flex flex-wrap gap-3 md:gap-6 items-center w-full md:w-[378px] mt-2 md:mt-0">
      {options.map((o) => {
        return (
          <div key={o.label} className="flex gap-2 items-center">
            <input {...input} type="radio" name={props.name} className="accent-primary-700" value={o.label} />
            <label>{o.label}</label>
          </div>
        )
      })}
    </div>
  </>)
}

const TextAreaForm: FC<{ label: string, placeholder: string } & UseFormType> = ({ label, placeholder, ...props }) => {
  const { field: input } = useController(props)
  return (
    <>
      <label className="text-xl">{label}</label>
      {/*@ts-ignore*/}
      <textarea {...input} placeholder={placeholder} className='rounded-[4px] py-2 bg-gray-100 w-full mt-4 md:mt-0 sm:w-[378px] px-4 text-sm focus:ring-0 focus:ring-transparent ring-0 focus:outline-none' rows={10} />
    </>
  )
}

export default Component
